// T-052 · "You can move up to $X" — the 48-hour hold calculator on Balances.
//
// Owner's rule (2026-09-28): every dollar that lands in an account starts its
// own 48-hour timer. While a deposit's timer runs, that deposit is HELD; what
// can move is the balance minus every held deposit. Old $10K + $12K landing
// today → $10K movable for 48 hours, then $22K.
//
// This is a CALCULATOR, not a gate (owner, same day): nothing reads it to
// allow or refuse a movement, and nothing is stored — the figure is derived
// from the live balance and the movements, so an admin correcting a balance,
// a date or an amount moves it automatically.
//
// What counts as a held deposit: a CONFIRMED inflow to the account that
// landed less than HOLD_MS ago. Pending rows are already outside the balance,
// so they are outside this too; denied rows did not happen. Outflows never
// enter the math — under the owner's rule only aged money can leave, so a
// withdrawal during the window always comes out of the old balance.
//
// "Landed" is the money's own time, not the logging time: Taekus draws carry
// the card transaction time (movement_time); imports carry effective_at; a
// hand-logged row has only a date and is treated as landing at the END of
// that day, so a deposit is never called aged before it could be.
//
// Pure logic, unit-tested under node (tests/investment-v2/hold-calc.test.mjs).

export const HOLD_HOURS = 48;
export const HOLD_MS = HOLD_HOURS * 3600 * 1000;

const ms = (iso) => {
  if (!iso) return NaN;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : NaN;
};

// When the money arrived, in epoch ms. NaN when the row has no usable time.
export function landedAt(m) {
  const withTime = ms(m.movement_time) || ms(m.effective_at);
  if (Number.isFinite(withTime) && withTime) return withTime;
  if (m.movement_date && /^\d{4}-\d{2}-\d{2}$/.test(String(m.movement_date).slice(0, 10))) {
    // Date-only: end of that local day (conservative — see header).
    const t = new Date(`${String(m.movement_date).slice(0, 10)}T23:59:59`).getTime();
    if (Number.isFinite(t)) return t;
  }
  return ms(m.created_at);
}

// Held deposits for one account at `now`, newest release last.
export function heldDeposits(accountId, movements, now = Date.now()) {
  const out = [];
  (movements || []).forEach((m) => {
    if (m.dest_account_id !== accountId) return;
    if (m.status === 'pending' || m.status === 'denied') return;
    const amount = Number(m.amount || 0);
    if (!(amount > 0)) return;
    const landed = landedAt(m);
    if (!Number.isFinite(landed)) return;
    const releasesAt = landed + HOLD_MS;
    if (releasesAt <= now) return; // aged out
    out.push({ id: m.id, amount, landedAt: landed, releasesAt, notes: m.notes || '' });
  });
  out.sort((a, b) => a.releasesAt - b.releasesAt);
  return out;
}

// The box's numbers for one balance row.
//   movable        balance minus held deposits, floored at 0; null when the
//                  balance is not set (no fiction on an unset account)
//   heldIn         Σ held deposits
//   holds          the deposits themselves (for the breakdown)
//   nextReleaseAt  when the next timer ends (ms) — null when nothing is held
export function movableNow(row, movements, now = Date.now()) {
  const holds = heldDeposits(row.accountId, movements, now);
  const heldIn = Math.round(holds.reduce((s, h) => s + h.amount, 0));
  const balance = row.isSet && Number.isFinite(Number(row.balance)) ? Number(row.balance) : null;
  return {
    movable: balance === null ? null : Math.max(0, Math.round(balance - heldIn)),
    heldIn,
    holds,
    nextReleaseAt: holds.length ? holds[0].releasesAt : null,
  };
}
